import React from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import ArrowUpRightIcon from '@atlaskit/icon/core/migration/arrow-up-right--editor-open';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import LightbulbIcon from '@atlaskit/icon/core/migration/lightbulb';
import { Flex, Grid } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';

import { HTV2LearningOpportunityCard } from './HTV2LearningOpportunityCard';

const EMPOWER_TEAM_LINK =
	'https://university.atlassian.com/student/path/861302-get-the-most-out-of-confluence?utm_source=in-product-banner&utm_medium=inapp&utm_campaign=P:uni-training*O:university*H:fy25-q2*I:confluence-in-product-onboarding-takeover-get-started*';

const BECOME_ADMIN_LINK =
	'https://university.atlassian.com/student/collection/850385/path/1062306?utm_source=in-product-banner&utm_medium=inapp&utm_campaign=P:uni-training*O:university*H:fy25-q2*I:confluence-in-product-onboarding-takeover-learn-how*';

const GET_CERTIFIED_LINK =
	'https://university.atlassian.com/student/activity/2227994-confluence-essentials-certification#/page/670ec053a1887954d18bb3c6?utm_source=in-product-banner&utm_medium=inapp&utm_campaign=P:uni-cert-exams*O:university*H:fy25-q2*I:confluence-in-product-onboarding-takeover-get-certified*';

const i18n = defineMessages({
	empowerYourTeamTitle: {
		id: 'experiment-home-onboarding-takeover-v2.empower-your-team.title',
		defaultMessage: 'Empower your team with the basics',
		description:
			'This will be shown as a title on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	empowerYourTeamDescription: {
		id: 'experiment-home-onboarding-takeover-v2.empower-your-team.description',
		defaultMessage:
			'Get your team up and running quickly in Confluence with short, self-paced courses.',
		description:
			'This will be shown as a description on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	empowerYourTeamCta: {
		id: 'experiment-home-onboarding-takeover-v2.empower-your-team.cta',
		defaultMessage: 'Get started',
		description:
			'This will be shown as a cta button on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	becomeAnEffectiveConfluenceAdminTitle: {
		id: 'experiment-home-onboarding-takeover-v2.become-an-effective-confluence-admin.title',
		defaultMessage: 'Become an effective Confluence admin',
		description:
			'This will be shown as a title on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	becomeAnEffectiveConfluenceDescription: {
		id: 'experiment-home-onboarding-takeover-v2.become-an-effective-confluence-admin.description',
		defaultMessage:
			'Create an exceptional content experience by managing users, spaces, and templates.',
		description:
			'This will be shown as a description on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	becomeAnEffectiveConfluenceCta: {
		id: 'experiment-home-onboarding-takeover-v2.become-an-effective-confluence-admin.cta',
		defaultMessage: 'Learn how',
		description:
			'This will be shown as a cta button on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	getCertifiedTitle: {
		id: 'experiment-home-onboarding-takeover-v2.get-certified.title',
		defaultMessage: 'Get certified in Confluence Essentials',
		description:
			'This will be shown as a title on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	getCertifiedDescription: {
		id: 'experiment-home-onboarding-takeover-v2.get-certified.description',
		defaultMessage:
			'Showcase your skills and stand out: validate your Confluence expertise with certification.',
		description:
			'This will be shown as a description on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	getCertifiedCta: {
		id: 'experiment-home-onboarding-takeover-v2.get-certified.cta',
		defaultMessage: 'Get certified',
		description:
			'This will be shown as a cta button on a card in the learning opportunities row embedded into the home screen as a part of onboarding.',
	},
	becomeAConfluencePro: {
		id: 'experiment-home-onboarding-takeover-v2.learning-opportunity.heading',
		defaultMessage: 'Become a Confluence pro',
		description: 'This will be shown as the section heading for the learning opportunity cards.',
	},
});

const HTV2LearningOpportunityCards = [
	{
		title: i18n.empowerYourTeamTitle,
		description: i18n.empowerYourTeamDescription,
		cta: i18n.empowerYourTeamCta,
		ctaIcon: ArrowUpRightIcon,
		link: EMPOWER_TEAM_LINK,
	},
	{
		title: i18n.becomeAnEffectiveConfluenceAdminTitle,
		description: i18n.becomeAnEffectiveConfluenceDescription,
		cta: i18n.becomeAnEffectiveConfluenceCta,
		ctaIcon: LightbulbIcon,
		link: BECOME_ADMIN_LINK,
	},
	{
		title: i18n.getCertifiedTitle,
		description: i18n.getCertifiedDescription,
		cta: i18n.getCertifiedCta,
		ctaIcon: IssuesIcon,
		link: GET_CERTIFIED_LINK,
	},
];

export const HTV2LearningOpportunityCardsList = () => {
	const { formatMessage } = useIntl();

	return (
		<>
			<Heading size="small">
				<FormattedMessage {...i18n.becomeAConfluencePro} />
			</Heading>
			<Flex direction="column">
				<Grid gap="space.100" templateColumns="repeat(3, 1fr)">
					{HTV2LearningOpportunityCards.map((learningOpportunityCardContent) => {
						return (
							<HTV2LearningOpportunityCard
								key={`${formatMessage(learningOpportunityCardContent.title)}`}
								{...learningOpportunityCardContent}
							/>
						);
					})}
				</Grid>
			</Flex>
		</>
	);
};
